import { useEffect, useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import type { MediaSummary, StructuredFilter } from "@media-viewer/shared";
import { api } from "../../api/client";
import { Modal } from "../Modal";
import { SearchBar } from "../SearchBar";

interface ExamplePickerProps {
  open: boolean;
  onClose: () => void;
  room: number;
  onAdd: (items: MediaSummary[]) => void;
}

export function ExamplePicker({ open, onClose, room, onAdd }: ExamplePickerProps) {
  const [filter, setFilter] = useState<StructuredFilter | null>(null);
  const [favorites, setFavorites] = useState(false);
  const [playlistId, setPlaylistId] = useState<number | null>(null);
  const [picked, setPicked] = useState<MediaSummary[]>([]);

  useEffect(() => {
    if (open) setPicked([]);
  }, [open]);

  const playlists = useQuery({ queryKey: ["playlists"], queryFn: api.listPlaylists, enabled: open });

  const library = useInfiniteQuery({
    queryKey: ["media", "list", { kind: "photo", set: favorites ? "favorites" : undefined, filter, picker: true }],
    queryFn: ({ pageParam }) =>
      api.listMedia({
        kind: "photo",
        set: favorites ? "favorites" : undefined,
        structured: filter,
        before: pageParam,
        limit: 60,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: open && playlistId === null,
  });

  const playlist = useQuery({
    queryKey: ["playlists", playlistId],
    queryFn: () => api.getPlaylist(playlistId!),
    enabled: open && playlistId !== null,
  });

  const items: MediaSummary[] =
    playlistId !== null
      ? (playlist.data?.items ?? []).filter((i) => i.kind === "photo")
      : (library.data?.pages.flatMap((p) => p.items) ?? []);
  const loading = playlistId !== null ? playlist.isPending : library.isPending;

  function toggle(item: MediaSummary) {
    setPicked((cur) => {
      if (cur.some((p) => p.id === item.id)) return cur.filter((p) => p.id !== item.id);
      if (cur.length >= room) return cur;
      return [...cur, item];
    });
  }

  const left = room - picked.length;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add example images from the collection"
      wide
      footer={
        <div className="flex w-full flex-wrap items-center justify-between gap-2">
          <span className="text-xs text-zinc-400">
            {picked.length} selected · room for {left} more with this model. Photos only — videos can't be used.
          </span>
          <div className="flex gap-2">
            <button onClick={onClose} className="rounded bg-zinc-800 px-3 py-1.5 text-zinc-200 hover:bg-zinc-700">
              Cancel
            </button>
            <button
              disabled={picked.length === 0}
              onClick={() => {
                onAdd(picked);
                onClose();
              }}
              className="rounded bg-emerald-700 px-3 py-1.5 text-emerald-100 hover:bg-emerald-600 disabled:opacity-50"
            >
              Add {picked.length} {picked.length === 1 ? "image" : "images"}
            </button>
          </div>
        </div>
      }
    >
      <div className="-mx-4 -mt-4">
        {playlistId === null && <SearchBar filter={filter} setFilter={setFilter} pinnedKind="photo" />}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
        <button
          aria-pressed={favorites}
          disabled={playlistId !== null}
          onClick={() => setFavorites((v) => !v)}
          className={
            "rounded px-3 py-1.5 disabled:opacity-40 " +
            (favorites ? "bg-zinc-700 text-zinc-100" : "bg-zinc-800 text-zinc-400 hover:text-zinc-200")
          }
        >
          Favorites
        </button>
        <label htmlFor="ex-playlist" className="sr-only">
          Playlist
        </label>
        <select
          id="ex-playlist"
          value={playlistId ?? ""}
          onChange={(e) => setPlaylistId(e.target.value ? Number(e.target.value) : null)}
          className="rounded border border-zinc-700 bg-zinc-800 px-2 py-1.5 text-zinc-200"
        >
          <option value="">All photos</option>
          {(playlists.data?.playlists ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              Playlist: {p.name}
            </option>
          ))}
        </select>
      </div>
      <div className="mt-3 max-h-[55vh] overflow-auto">
        {loading ? (
          <p className="py-8 text-center text-zinc-500">Loading…</p>
        ) : items.length === 0 ? (
          <p className="py-8 text-center text-zinc-500">No photos here.</p>
        ) : (
          <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-5">
            {items.map((item) => {
              const idx = picked.findIndex((p) => p.id === item.id);
              const on = idx >= 0;
              return (
                <button
                  key={item.id}
                  aria-pressed={on}
                  aria-label={on ? `Photo ${item.id}, selected as image ${idx + 1}` : `Photo ${item.id}`}
                  disabled={!on && left <= 0}
                  onClick={() => toggle(item)}
                  className={
                    "relative aspect-square overflow-hidden rounded bg-zinc-800 disabled:opacity-40 " +
                    (on ? "ring-2 ring-sky-400" : "hover:ring-1 hover:ring-zinc-500")
                  }
                >
                  <img src={item.thumbUrl} alt="" loading="lazy" className="h-full w-full object-cover" />
                  {on && (
                    <span className="absolute left-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-sky-500 text-xs font-semibold text-white">
                      {idx + 1}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
        {playlistId === null && library.hasNextPage && (
          <div className="mt-3 text-center">
            <button
              onClick={() => void library.fetchNextPage()}
              disabled={library.isFetchingNextPage}
              className="rounded bg-zinc-800 px-3 py-1.5 text-xs text-zinc-300 hover:bg-zinc-700 disabled:opacity-50"
            >
              {library.isFetchingNextPage ? "Loading…" : "Load more"}
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}
